import { PERMS_VALUE, type PermsKey } from '@/platform/identity/perms';
import { useUserStore } from '@/platform/identity/store';

// Composable form of permission checks for v3 native modules. Reads the
// Pinia user store directly; on legacy side the same data flows via
// Vuex write-through (see plan §9) — both end up reading the same wire
// values from /account/user-info.
export function usePermission() {
  const user = useUserStore();

  // Checks by permission NAME (PermsKey) and translates to the backend wire
  // code that `perms` actually holds — matching legacy haveAccess, which did
  // `perms[name]` → code before the membership test. `allObj@allAct` is the
  // global passport: its holder satisfies every check.
  function hasPerm(key: PermsKey): boolean {
    return (
      user.perms.includes(PERMS_VALUE['allObj@allAct']) || user.perms.includes(PERMS_VALUE[key])
    );
  }

  // Exact-match check (legacy `exactlyHasPerm`): the perm must be held
  // literally — the `allObj@allAct` passport does NOT satisfy it. Used where a
  // capability is gated on holding a specific perm even for admins (e.g. the
  // org-level `org@all` payment/account-management gate).
  function hasExactPerm(key: PermsKey): boolean {
    return user.perms.includes(PERMS_VALUE[key]);
  }

  function hasDataPerm(key: string, resourceId: string): boolean {
    return user.dataPerms?.[key]?.includes(resourceId) ?? false;
  }

  return { hasPerm, hasExactPerm, hasDataPerm };
}
